import { AfterViewInit, ChangeDetectionStrategy, Component, computed, input, OnInit, output, signal, viewChild } from '@angular/core';
import {
  Address,
  IBankDetails,
  IERechnungsInfo,
  IFormModel,
  IIBANFormConfig,
  IInvoiceConfig,
  IOlavEntry,
  IVertragskonto,
  RECHNUNGSVERSAND_ERECHNUNG,
  RECHNUNGSVERSAND_POST,
  ZAHLUNGSMETHODE_BANKEINZUG,
  ZAHLUNGSMETHODE_ZAHLSCHEIN,
} from '@mwe/models';
import { InternationalAddressInputComponent } from '../../address-input/international-address-input.component';
import { IBANFormComponent } from '../../iban-form/iban-form.component';
import { Validators } from '@angular/forms';
import { equalEmailValidator, getAddressGroupFormatted, getEmailValidatorWithRegExp } from '@mwe/utils';
import { FormComponent } from '../../form/form.component';
import { BasicCardComponent } from '../../cards/basic-card/basic-card.component';

import { BasicCardTitleComponent } from '../../cards/basic-card/basic-card-title/basic-card-title.component';
import { TranslateDirective } from '@ngx-translate/core';
import { InfoboxComponent } from '../../infobox/infobox.component';

@Component({
  selector: 'mwe-invoice-information',
  templateUrl: './invoice-information.component.html',
  styles: [],
  imports: [
    BasicCardComponent,
    BasicCardTitleComponent,
    FormComponent,
    TranslateDirective,
    IBANFormComponent,
    InternationalAddressInputComponent,
    InfoboxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceInformationComponent implements OnInit, AfterViewInit {
  ibanFormRef = viewChild(IBANFormComponent);
  emailFormRef = viewChild<FormComponent>('emailForm');
  paymentFormRef = viewChild<FormComponent>('paymentForm');
  invoiceFormRef = viewChild<FormComponent>('invoiceForm');
  internationalAddressInputRef = viewChild(InternationalAddressInputComponent);

  invoiceInfoConfig = input<IInvoiceConfig>();
  isTitleVisible = input<boolean>(true);
  isSepaOrZahlscheinSliderVisible = input<boolean | 'readonly'>(true);
  isERechnungVisible = input<boolean>(true);
  isERechnungSliderVisible = input<boolean>(true);
  isInternationalAddressInputVisible = input<boolean>(false);
  newInvoiceAddressTitle = input<string>('invoiceInformation.newInvoiceAddress.title');
  allowEmailChange = input<boolean>(true);
  productCategories = input<string[]>([]);

  onSetInvoiceData = output<IVertragskonto>();
  onSetErechnungData = output<IERechnungsInfo>();
  onIBANFormSuccess = output<IBankDetails>();
  onIBANFormError = output<any>();

  ibanConfig = signal<IIBANFormConfig | undefined>(undefined);
  erechnungData = signal<IERechnungsInfo | undefined>(undefined);
  invoiceData = signal<IVertragskonto | undefined>(undefined);

  toggleInvoiceLabel = signal<string | undefined>(undefined);
  toggleERechnungLabel = signal<string | undefined>(undefined);
  address = signal<string | undefined>(undefined);
  overrideEmailAddress = signal<string | undefined>(undefined);

  invoiceSEPA = signal<boolean>(true);
  erechnungEnabled = signal<boolean>(true);

  isSepaPflicht = signal<boolean>(false);
  isERechnungsPflicht = signal<boolean>(false);

  sepaTitle = signal<string>('invoiceInformation.invoiceHow.title');
  eRechnungTitle = signal<string>('invoiceInformation.erechnung.title');
  toggleOnInvoiceKey = signal<string>('invoiceInformation.invoiceHow.toggleSepa.on');
  toggleOffInvoiceKey = signal<string>('invoiceInformation.invoiceHow.toggleSepa.off');
  toggleOnERechnungKey = signal<string>('invoiceInformation.erechnung.toggleERechnung.on');
  toggleOffERechnungKey = signal<string>('invoiceInformation.erechnung.toggleERechnung.off');
  eRechnungAddressChangeKey = signal<string>('invoiceInformation.erechnung.address-change-info');

  addressChangeWarningPlaceholders = signal<Map<string, string>>(new Map<string, string>());

  paymentSelectionForm = signal<IFormModel>(undefined);
  invoiceSelectionForm = signal<IFormModel>(undefined);
  emailAddressForm = signal<IFormModel>(undefined);
  readonlyAddressForm = signal<IFormModel>(undefined);
  emailAddressFormValid = signal<boolean>(undefined);

  shouldShowEmailChangeForm = computed(() => this.allowEmailChange() && (this.erechnungEnabled() || this.isERechnungsPflicht()));
  paymentOptions = computed(() =>
    this.isSepaPflicht() ? [ZAHLUNGSMETHODE_BANKEINZUG] : [ZAHLUNGSMETHODE_BANKEINZUG, ZAHLUNGSMETHODE_ZAHLSCHEIN],
  );
  invoiceOptions = computed(() => {
    const options = [RECHNUNGSVERSAND_POST];
    if (this.isERechnungsPflicht()) return [RECHNUNGSVERSAND_ERECHNUNG];
    if (this.isERechnungVisible()) options.push(RECHNUNGSVERSAND_ERECHNUNG);
    return options;
  });
  showPaymentSelectForm = computed(() => this.paymentOptions()?.length > 1 && this.isSepaOrZahlscheinSliderVisible() === true);
  showInvoiceSelectForm = computed(() => this.invoiceOptions()?.length > 1 && this.isERechnungSliderVisible());
  showInvoiceAddressChangeWarning = computed(
    () => this.erechnungEnabled() && this.invoiceInfoConfig().invoiceAddressChangesTo && !this.isERechnungsPflicht(),
  );

  ngOnInit(): void {
    this.initConfigData();
    this.initInvoiceForm();
    this.initERechnungForm();
    this.initEmailAddressForm();
    this.initReadonlyAddressForm();
  }

  ngAfterViewInit(): void {
    if (this.isInternationalAddressInputVisible()) {
      this.checkAddress();
    }
  }

  async validate(): Promise<void> {
    const validationResults: boolean[] = [];
    if (this.showPaymentSelectForm()) {
      this.paymentFormRef().fireValidation();
      validationResults.push(this.paymentFormRef().isValid());
    }
    if (this.showInvoiceSelectForm()) {
      this.invoiceFormRef().fireValidation();
      validationResults.push(this.invoiceFormRef().isValid());
    }
    if (this.shouldShowEmailChangeForm()) {
      this.emailFormRef().fireValidation();
      validationResults.push(this.emailFormRef().isValid());
    }
    if (this.isInternationalAddressInputVisible()) {
      validationResults.push(this.validateAddress());
    }
    if (this.invoiceSEPA()) {
      await this.ibanFormRef().validateForm();
    }
    if (!validationResults.every(e => e)) {
      throw new Error('Form is not valid');
    }
  }

  initConfigData(): void {
    this.toggleInvoiceLabel.set(this.toggleOnInvoiceKey());
    this.toggleERechnungLabel.set(this.toggleOnERechnungKey());
    this.invoiceData.set(this.invoiceInfoConfig().invoiceData);
    this.erechnungData.set(this.invoiceInfoConfig().erechnungData);
    this.address.set(this.invoiceInfoConfig().address);
    this.isERechnungsPflicht.set(this.invoiceInfoConfig().eRechnungMandatory);
    this.isSepaPflicht.set(this.invoiceInfoConfig().invoiceMandatory);
  }

  initERechnungForm(): void {
    if (!this.erechnungData()) {
      this.erechnungData.set({ isERechnungsEmpfaenger: true, rechnungEmail: null });
    }

    if (this.isERechnungsPflicht()) {
      this.onToggleERechnung(true);
    }

    this.erechnungData().rechnungEmail = this.invoiceInfoConfig().rechnungsMail;

    this.erechnungEnabled.set(this.erechnungData().isERechnungsEmpfaenger);
    this.toggleERechnungLabel.set(this.erechnungEnabled() ? this.toggleOnERechnungKey() : this.toggleOffERechnungKey());
    this.addressChangeWarningPlaceholders.update(addr => {
      const next = new Map(addr);
      next.set('contact', this.invoiceInfoConfig().invoiceAddressChangesTo);
      return next;
    });
    this.initInvoiceSelectionForm(this.erechnungEnabled() ? 'eRechnung' : 'Post');
  }

  checkAddress(): void {
    this.address.set(getAddressGroupFormatted(this.getAddress(), true));
    this.initReadonlyAddressForm();
  }

  onPaymentFormChange({ paymentMethod }): void {
    this.showPaymentSelectForm() && this.onToggleIBAN(paymentMethod === ZAHLUNGSMETHODE_BANKEINZUG);
  }

  onInvoiceFormChange({ invoiceMethod }): void {
    this.showInvoiceSelectForm() && this.onToggleERechnung(invoiceMethod === 'eRechnung');
  }

  onEmailFormChange({ email }, isValid: boolean): void {
    this.emailAddressFormValid.set(isValid);
    this.overrideEmailAddress.set(isValid ? email : undefined);
    if (isValid) {
      this.onSetErechnungData.emit({
        ...this.erechnungData(),
        rechnungEmail: this.overrideEmailAddress() ? this.overrideEmailAddress() : this.erechnungData().rechnungEmail,
      });
    }
  }

  onToggleIBAN(checked: boolean, init?: boolean): void {
    this.invoiceSEPA.set(checked);
    this.toggleInvoiceLabel.set(this.invoiceSEPA() ? this.toggleOnInvoiceKey() : this.toggleOffInvoiceKey());
    if (!this.invoiceSEPA()) {
      this.invoiceData.update(inv => ({ ...inv, zahlungsmethode: ZAHLUNGSMETHODE_ZAHLSCHEIN }));
      this.invoiceData.update(inv => ({ ...inv, iban: null }));
      this.invoiceData.update(inv => ({ ...inv, bic: null }));
    } else {
      this.invoiceData.update(inv => ({ ...inv, zahlungsmethode: ZAHLUNGSMETHODE_BANKEINZUG }));
    }
    this.onSetInvoiceData.emit(this.invoiceData());
  }

  onToggleERechnung(checked: boolean): void {
    this.erechnungEnabled.set(checked);
    this.toggleERechnungLabel.set(this.erechnungEnabled() ? this.toggleOnERechnungKey() : this.toggleOffERechnungKey());
    this.erechnungData.update(inv => ({ ...inv, isERechnungsEmpfaenger: checked }));
    this.onSetErechnungData.emit({
      ...this.erechnungData(),
      rechnungEmail: this.overrideEmailAddress() ? this.overrideEmailAddress() : this.erechnungData().rechnungEmail,
    });
  }

  ibanFormError(data?: any): void {
    this.invoiceData.update(inv => ({ ...inv, iban: null }));
    this.onIBANFormError.emit(data);
    this.onSetInvoiceData.emit(this.invoiceData());
  }

  ibanFormSubmitted(data: IBankDetails): void {
    this.invoiceData.update(inv => ({ ...inv, iban: data.iban }));
    this.onIBANFormSuccess.emit(data);
    this.onSetInvoiceData.emit(this.invoiceData());
  }

  async validateForm(): Promise<void> {
    await this.ibanFormRef()?.validateForm();
  }

  validateAddress(): boolean {
    return this.internationalAddressInputRef()?.validate();
  }

  getAddress(): Address {
    return this.internationalAddressInputRef()?.getAddress();
  }

  getOlavEntry(): IOlavEntry {
    return this.internationalAddressInputRef()?.getOlavEntry();
  }

  private initInvoiceForm(): void {
    if (this.invoiceData()) {
      this.invoiceSEPA.set(this.invoiceData().zahlungsmethode === ZAHLUNGSMETHODE_BANKEINZUG);
    } else {
      this.invoiceData.set({ vertragskontoId: null, blz: null, kontonummer: null, zahlungsmethode: null, iban: null });
    }
    if (this.isSepaPflicht() || this.invoiceSEPA()) {
      this.onToggleIBAN(true, true);
    }
    this.toggleInvoiceLabel.set(this.invoiceSEPA() ? this.toggleOnInvoiceKey() : this.toggleOffInvoiceKey());
    this.ibanConfig.set({ iban: this.invoiceData().iban });
    this.initPaymentSelectionForm(this.invoiceSEPA() ? ZAHLUNGSMETHODE_BANKEINZUG : ZAHLUNGSMETHODE_ZAHLSCHEIN);
  }

  private initEmailAddressForm(): void {
    this.emailAddressForm.set({
      inputs: [
        {
          dataType: 'email',
          name: 'email',
          initialValue: this.erechnungData()?.rechnungEmail || '',
          validators: [Validators.required, Validators.email, getEmailValidatorWithRegExp()],
          labelKey: 'invoiceInformation.email.label',
          validate: true,
          validationErrorLabelKey: 'invoiceInformation.email.validationError',
          componentType: 'emailToConfirm',
          updateOn: 'change',
          isReadonly: !this.shouldShowEmailChangeForm(),
          emailConfirmationOptions: {
            referenceEmailIsInitialValue: true,
          },
        },
      ],
    });
    if (this.shouldShowEmailChangeForm()) {
      this.emailAddressForm.update(f => ({
        ...f,
        inputs: [
          ...f.inputs,
          {
            dataType: 'email',
            name: 'emailConfirmation',
            initialValue: '',
            validators: [Validators.required, equalEmailValidator('email')],
            labelKey: 'invoiceInformation.email.confirmationLabel',
            validate: true,
            validationErrorLabelKey: 'invoiceInformation.email.confirmationError',
            avoidCopyPaste: true,
            isHidden: true,
            componentType: 'emailConfirmationField',
            updateOn: 'change',
          },
        ],
      }));
    }
  }

  private initReadonlyAddressForm(): void {
    if (!this.address()) {
      return;
    }
    this.readonlyAddressForm.set({
      inputs: [
        {
          name: 'address',
          initialValue: this.address(),
          labelKey: 'invoiceInformation.invoiceOptions.postal',
          isReadonly: true,
        },
      ],
    });
  }

  private initPaymentSelectionForm(initialValue: string): void {
    if (this.isSepaOrZahlscheinSliderVisible() === 'readonly') {
      this.paymentSelectionForm.set({
        inputs: [
          {
            name: 'paymentMethod',
            initialValue,
            validators: [Validators.required],
            labelKey: 'invoiceInformation.paymentOptions.title',
            isReadonly: true,
          },
        ],
      });
    } else {
      this.paymentSelectionForm.set({
        inputs: [
          {
            name: 'paymentMethod',
            initialValue,
            options: this.paymentOptions()?.map(option => ({
              value: option,
              text: `invoiceInformation.paymentOptions.${option}`,
            })),
            componentType: 'select',
            validators: [Validators.required],
            labelKey: 'invoiceInformation.paymentOptions.title',
          },
        ],
      });
    }
  }

  private initInvoiceSelectionForm(initialValue: string): void {
    this.invoiceSelectionForm.set({
      inputs: [
        {
          name: 'invoiceMethod',
          initialValue,
          labelKey: 'invoiceInformation.invoiceOptions.title',
          options: this.invoiceOptions()?.map(option => ({
            value: option,
            text: `invoiceInformation.invoiceOptions.${option}`,
          })),
          componentType: 'select',
          validators: [Validators.required],
        },
      ],
    });
  }
}
